import { useTranslation } from "@/features/i18n";
import { useAuthOptional } from "@/hooks/useAuth";
import { useMaintenance } from "@/hooks/useMaintenance";
import React from "react";

interface MaintenanceWrapperProps {
  children: React.ReactNode;
}

const MaintenanceWrapper: React.FC<MaintenanceWrapperProps> = ({
  children,
}) => {
  const auth = useAuthOptional();
  const user = auth?.user ?? null;
  const isLoading = auth?.isLoading ?? false;
  const { isMaintenanceMode, config } = useMaintenance();
  const { t } = useTranslation("common");

  // Wenn User eingeloggt ist und Auto-Logout aktiviert ist, melde ab (Hook immer aufrufen)
  React.useEffect(() => {
    if (!isMaintenanceMode) return;
    if (!isLoading && user && config.autoLogout) {
      // User abmelden
      const logout = async () => {
        const { supabase } = await import("@/integrations/supabase/client");
        await supabase.auth.signOut();
      };
      logout();
    }
  }, [isMaintenanceMode, user, isLoading, config.autoLogout]);

  // Wenn Wartungsarbeiten nicht aktiv sind, zeige normale Komponente
  if (!isMaintenanceMode) {
    return <>{children}</>;
  }

  // Wartungsmeldung anzeigen
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full text-gray-800 border border-gray-200 shadow-2xl shadow-gray-200/50 text-center">
        <div className="relative mb-4">
          <img
            src="/media/couple-up-logo-display.webp"
            alt="CoupleUp Logo"
            className="w-32 h-32 mx-auto"
          />
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 text-center">
          <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>

          <div className="text-orange-800 font-bold mb-2">{config.title}</div>

          <div className="text-orange-700 text-sm">{config.message}</div>
        </div>

        {config.showButtons && (
          <div className="mt-6 space-y-4">
            {user && config.autoLogout && (
              <div className="text-gray-600 text-sm mb-4">
                {t("maintenance.autoLoggedOut")}
              </div>
            )}

            <button
              disabled
              className="w-full bg-gray-300 text-gray-500 font-bold py-5 px-6 rounded-2xl cursor-not-allowed flex items-center justify-center space-x-3 opacity-60"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                />
              </svg>
              <span>{t("maintenance.loginDisabled")}</span>
            </button>

            <button
              disabled
              className="w-full bg-gray-300 text-gray-500 font-bold py-5 px-6 rounded-2xl cursor-not-allowed flex items-center justify-center space-x-3 opacity-60"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                />
              </svg>
              <span>{t("maintenance.registrationDisabled")}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenanceWrapper;
